import {Trans} from '@lingui/macro';
import React from 'react';

import {
  IntegrationTypeCategoriesEnum,
  IntegrationTypeModel,
  IntegrationTypesSupportedTypesEnum,
} from '../models/api/integrationTypesModel';

/*
 * Constants.
 */

const integrationCategoryNameRenderMap: {[t in IntegrationTypeCategoriesEnum]: React.ReactNode} = {
  [IntegrationTypeCategoriesEnum.ANALYTICS]: (
    <Trans id="integration-types-category-analytics-title">Analytics</Trans>
  ),
  [IntegrationTypeCategoriesEnum.ANALYTICS_REPORTING]: (
    <Trans id="integration-types-category-analytics-reporting-title">Analytics &amp; Reporting</Trans>
  ),
  [IntegrationTypeCategoriesEnum.BILLING_INVOICING]: (
    <Trans id="integration-types-billing-billing-invoicing-title">Billing &amp; Invoicing</Trans>
  ),
  [IntegrationTypeCategoriesEnum.BOTS]: <Trans id="integration-types-category-bots-title">Bots</Trans>,
  [IntegrationTypeCategoriesEnum.BOTS_WORKFLOW_AUTOMATION]: (
    <Trans id="integration-types-category-bots-workflow-automation-title">
      Bots &amp; Workflow Automation
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.CHANNEL]: (
    <Trans id="integration-types-category-channel-title">Channel</Trans>
  ),
  [IntegrationTypeCategoriesEnum.CHANNELS_MESSAGING]: (
    <Trans id="integration-types-category-channels-messaging-title">Channels &amp; Messaging</Trans>
  ),
  [IntegrationTypeCategoriesEnum.CLOUD_STORAGE]: (
    <Trans id="integration-types-category-cloud-storage-title">Cloud Storage</Trans>
  ),
  [IntegrationTypeCategoriesEnum.COMMUNICATION]: (
    <Trans id="integration-types-category-communication-title">Communication</Trans>
  ),
  [IntegrationTypeCategoriesEnum.CONTACTS]: (
    <Trans id="integration-types-category-contacts-title">Contacts</Trans>
  ),
  [IntegrationTypeCategoriesEnum.CRM_ECOMMERCE]: (
    <Trans id="integration-types-category-crm-ecommerce-title">CRM &amp; Ecommerce</Trans>
  ),
  [IntegrationTypeCategoriesEnum.CRM_SALES_MANAGEMENT]: (
    <Trans id="integration-types-category-crm-sales-management-title">CRM &amp; Sales Management</Trans>
  ),
  [IntegrationTypeCategoriesEnum.CUSTOMER_SUPPORT]: (
    <Trans id="integration-types-category-customer-support-title">Customer Support</Trans>
  ),
  [IntegrationTypeCategoriesEnum.DATA]: <Trans id="integration-types-category-data-title">Data</Trans>,
  [IntegrationTypeCategoriesEnum.DATA_PRIVACY]: (
    <Trans id="integration-types-category-data-privacy-title">Data Privacy</Trans>
  ),
  [IntegrationTypeCategoriesEnum.DATA_SYNC_ENRICHMENT]: (
    <Trans id="integration-types-category-data-sync-enrichment-title">Data Sync &amp; Enrichment</Trans>
  ),
  [IntegrationTypeCategoriesEnum.DEVELOPER_TOOLS]: (
    <Trans id="integration-types-category-developer-tools-title">Developer Tools</Trans>
  ),
  [IntegrationTypeCategoriesEnum.ECOMMERCE]: (
    <Trans id="integration-types-category-ecommerce-title">Ecommerce</Trans>
  ),
  [IntegrationTypeCategoriesEnum.ISSUE_TRACKING]: (
    <Trans id="integration-types-category-issue-tracking-title">Issue Tracking</Trans>
  ),
  [IntegrationTypeCategoriesEnum.KNOWLEDGE_CONTENT]: (
    <Trans id="integration-types-category-knowledge-content-title">Knowledge &amp; Content</Trans>
  ),
  [IntegrationTypeCategoriesEnum.MARKETING]: (
    <Trans id="integration-types-category-marketing-title">Marketing</Trans>
  ),
  [IntegrationTypeCategoriesEnum.MESSAGING]: (
    <Trans id="integration-types-category-messaging-title">Messaging</Trans>
  ),
  [IntegrationTypeCategoriesEnum.PAYMENTS_INVOICING]: (
    <Trans id="integration-types-category-payments-invoicing-title">Payments &amp; Invoicing</Trans>
  ),
  [IntegrationTypeCategoriesEnum.PRODUCTIVITY]: (
    <Trans id="integration-types-category-productivity-title">Productivity</Trans>
  ),
  [IntegrationTypeCategoriesEnum.PROJECT_MANAGEMENT]: (
    <Trans id="integration-types-category-project-management-title">Project Management</Trans>
  ),
  [IntegrationTypeCategoriesEnum.PROJECT_MANAGEMENT_ISSUE_TRACKING]: (
    <Trans id="integration-types-category-project-management-issue-tracking-title">
      Project Management &amp; Issue Tracking
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.SURVEYS_FEEDBACK]: (
    <Trans id="integration-types-category-surveys-feedback-title">Surveys &amp; Feedback</Trans>
  ),
  [IntegrationTypeCategoriesEnum.VOICE]: <Trans id="integration-types-category-voice-title">Voice</Trans>,
  [IntegrationTypeCategoriesEnum.VOICE_TELEPHONY]: (
    <Trans id="integration-types-category-voice-telephony-title">Voice &amp; Telephony</Trans>
  ),
  [IntegrationTypeCategoriesEnum.ESSENTIALS]: (
    <Trans id="integration-types-category-essentials-title">Essentials</Trans>
  ),
};

const integrationCategoryDescriptionRenderMap: {[t in IntegrationTypeCategoriesEnum]: React.ReactNode} = {
  [IntegrationTypeCategoriesEnum.ANALYTICS]: (
    <Trans id="integration-types-category-analytics-description">
      Get insight and visibility into team performance.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.ANALYTICS_REPORTING]: (
    <Trans id="integration-types-category-analytics-reporting-description">
      Get insight and visibility into team performance.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.BILLING_INVOICING]: (
    <Trans id="integration-types-category-billing-invoicing-description">
      Track invoices and payments from your customers.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.BOTS]: (
    <Trans id="integration-types-category-bots-description">
      Deflect support inquiries and automate workflows.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.BOTS_WORKFLOW_AUTOMATION]: (
    <Trans id="integration-types-category-bots-workflow-automation-description">
      Deflect support inquiries and automate workflows.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.CHANNEL]: (
    <Trans id="integration-types-category-channel-description">
      Reach customers across social media, SMS, live chat and more.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.CHANNELS_MESSAGING]: (
    <Trans id="integration-types-category-channels-messaging-description">
      Reach customers across social media, SMS, live chat and more.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.CLOUD_STORAGE]: (
    <Trans id="integration-types-category-cloud-storage-description">
      Store documents things in the cloud.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.COMMUNICATION]: (
    <Trans id="integration-types-category-communication-description">
      Reach customers across social media, SMS, live chat and more.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.CONTACTS]: (
    <Trans id="integration-types-category-contacts-description">
      Keep your contacts, accounts, analytics and more in sync with your CRM or data warehouse.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.CRM_ECOMMERCE]: (
    <Trans id="integration-types-category-crm-ecommerce-description">
      Bring context on customers, accounts, orders and more right into your inbox.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.CRM_SALES_MANAGEMENT]: (
    <Trans id="integration-types-category-crm-sales-management-description">
      Bring context on customers, accounts, orders and more right into your inbox.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.CUSTOMER_SUPPORT]: (
    <Trans id="integration-types-category-customer-support-description">
      Assist customers with support inquires.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.DATA]: (
    <Trans id="integration-types-category-data-description">
      Keep your contacts, accounts, analytics and more in sync with your CRM or data warehouse.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.DATA_PRIVACY]: (
    <Trans id="integration-types-category-data-privacy-description">
      Ensure compliance with privacy standards and protocols.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.DATA_SYNC_ENRICHMENT]: (
    <Trans id="integration-types-category-data-sync-enrichment-description">
      Keep your contacts, accounts, analytics and more in sync with your CRM or data warehouse.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.DEVELOPER_TOOLS]: (
    <Trans id="integration-types-category-developer-tools-description">
      Create tasks and get updates on project statuses.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.ECOMMERCE]: (
    <Trans id="integration-types-category-ecommerce-description">
      Bring context on customers, accounts, orders and more right into your inbox.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.ISSUE_TRACKING]: (
    <Trans id="integration-types-category-issue-tracking-description">
      Create tasks and get updates on project statuses.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.KNOWLEDGE_CONTENT]: (
    <Trans id="integration-types-category-knowledge-content-description">
      Easily access knowledge base content to improve support quality.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.MARKETING]: (
    <Trans id="integration-types-category-marketing-description">
      Increase efficiency of day-to-day tasks.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.MESSAGING]: (
    <Trans id="integration-types-category-messaging-description">
      Reach customers across social media, SMS, live chat and more.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.PAYMENTS_INVOICING]: (
    <Trans id="integration-types-category-payments-invoicing-description">
      Track invoices and payments from your customers.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.PRODUCTIVITY]: (
    <Trans id="integration-types-category-productivity-description">
      Increase efficiency of day-to-day tasks.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.PROJECT_MANAGEMENT]: (
    <Trans id="integration-types-category-project-management-description">
      Create tasks and get updates on project statuses.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.PROJECT_MANAGEMENT_ISSUE_TRACKING]: (
    <Trans id="integration-types-category-project-management-issue-tracking-description">
      Create tasks and get updates on project statuses.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.SURVEYS_FEEDBACK]: (
    <Trans id="integration-types-category-surveys-feedback-description">
      Understand customer health and satisfaction.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.VOICE]: (
    <Trans id="integration-types-category-voice-description">
      Make and receive calls from your inbox, and sync SMS messages and call logs.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.VOICE_TELEPHONY]: (
    <Trans id="integration-types-category-voice-telephony-description">
      Make and receive calls from your inbox, and sync SMS messages and call logs.
    </Trans>
  ),
  [IntegrationTypeCategoriesEnum.ESSENTIALS]: (
    <Trans id="integration-types-category-essentials-description">Essential plugins and channels.</Trans>
  ),
};

const integrationTypeSupportedTypeRenderMap: {[t in IntegrationTypesSupportedTypesEnum]: React.ReactNode} = {
  [IntegrationTypesSupportedTypesEnum.CHANNEL]: (
    <Trans id="integration-types-supported-type-channel">Channel</Trans>
  ),
  [IntegrationTypesSupportedTypesEnum.OTHER]: (
    <Trans id="integration-types-supported-type-other">Other</Trans>
  ),
  [IntegrationTypesSupportedTypesEnum.PLUGIN]: (
    <Trans id="integration-types-supported-type-plugin">Plugin</Trans>
  ),
};

/*
 * Helpers.
 */

export function renderIntegrationTypeCategoryName(category: IntegrationTypeCategoriesEnum) {
  return integrationCategoryNameRenderMap[category];
}

export function renderIntegrationTypeCategoryDescription(category: IntegrationTypeCategoriesEnum) {
  return integrationCategoryDescriptionRenderMap[category];
}

export function renderIntegrationTypeSupportedType(type: IntegrationTypesSupportedTypesEnum) {
  return integrationTypeSupportedTypeRenderMap[type];
}

export function isIntegrationTypeEnabled(integrationType?: IntegrationTypeModel) {
  if (!integrationType) {
    return false;
  }

  // If we have an integration type and we have an oauth connection return true.
  // If the plugin has oauth and has a plugin, ignore the oauth state.
  if (
    integrationType &&
    integrationType.oauthConnected &&
    !integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.PLUGIN)
  ) {
    return true;
  }

  // If the integration is only a channel only we default to true since channels can not be
  // enabled or disabled.
  if (integrationType) {
    return (
      integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.CHANNEL) &&
      integrationType.supportedTypes.length === 1
    );
  }

  return false;
}
